import React, { useState, useEffect, forwardRef, useRef } from "react";
import {
  Dialog, DialogContent, DialogTitle, IconButton, Box, TextField,
  MenuItem, Select, FormControl, InputLabel, FormHelperText,
  Checkbox, Typography, FormLabel, FormControlLabel,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import Slide, { SlideProps } from '@mui/material/Slide';
import DoneIcon from '@mui/icons-material/Done';
import Lottie from "lottie-react";
import SuccessLottie from "@/assets/SuccessGif.json";
import LightsImage from "@/assets/Lights.png";
import UnlockBoxImage from "@/assets/Unlock.png";
import { sendEvent } from "../lib/googleSheet";
import { getOrCreateUserId } from "../lib/session";

interface WaitlistPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WaitlistFormData) => void;
}

export interface WaitlistFormData {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  investsInMF: string;
  monthlyIncome: string;
}

const Transition = forwardRef<unknown, SlideProps>((props, ref) => (
  <Slide direction="down" ref={ref} {...props} />
));

const MONTHLY_INCOME_OPTIONS = [
  { value: "8,00,000 - 12,00,000", label: "₹8,00,000 - ₹12,00,000" },
  { value: "12,00,000 - 25,00,000", label: "₹12,00,000 - ₹25,00,000" },
  { value: "25,00,000 - 50,00,000", label: "₹25,00,000 - ₹50,00,000" },
  { value: "above-50,00,000", label: "Above ₹50,00,000" }
];

const INITIAL_FORM_DATA: WaitlistFormData = {
  fullName: "", phoneNumber: "", emailAddress: "", investsInMF: "", monthlyIncome: ""
};

const validators: Record<keyof WaitlistFormData, (value: string) => string> = {
  fullName: (value) => !value.trim() ? "Full name is required" :
    !/^[A-Za-z\s]+$/.test(value) ? "Please enter a valid name" :
      value.trim().length < 2 ? "Please enter a valid name" : "",

  phoneNumber: (value) => !value.trim() ? "please enter a valid mobile number" :
    !/^\d{10}$/.test(value) ? "please enter a valid mobile number" :
      !/^[6-9]/.test(value) ? "please enter a valid mobile number" : "",

  emailAddress: (value) => !value.trim() ? "Please enter a valid email address" :
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "Please enter a valid email address" : "",

  investsInMF: (value) => !value ? "Please select an option" : "",
  monthlyIncome: () => ""
};

const fieldProcessors: Record<string, (value: string) => string> = {
  phoneNumber: (value) => {
    const clean = value.replace(/\D/g, "").slice(0, 10);
    if (clean.length === 1 && !/^[6-9]/.test(clean)) return "";
    if (clean.length > 1 && !/^[6-9]/.test(clean)) return value.slice(0, -1);
    return clean;
  },
  fullName: (value) => value.replace(/[^A-Za-z\s]/g, "")
};

const FORM_FIELDS = [
  { key: "fullName" as const, label: "Name", type: "text", placeholder: "Enter your full name" },
  { key: "emailAddress" as const, label: "E-Mail", type: "email", placeholder: "Enter your email address" },
  { key: "phoneNumber" as const, label: "Mobile", type: "tel", placeholder: "Enter 10-digit mobile number", inputProps: { maxLength: 10 } }
];

export const WaitlistPopup = ({ isOpen, onClose, onSubmit }: WaitlistPopupProps) => {
  const [formData, setFormData] = useState<WaitlistFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Partial<WaitlistFormData>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof WaitlistFormData, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const flipBackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const desktopCloseTimerRef = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen && hasSubmitted) {
      if (isMobile && isFlipped) {
        if (flipBackTimerRef.current) clearTimeout(flipBackTimerRef.current);
        flipBackTimerRef.current = setTimeout(() => {
          handleClose();
        }, 5000);
      }
      if (!isMobile && hasSubmitted) {
        if (desktopCloseTimerRef.current) clearTimeout(desktopCloseTimerRef.current);
        desktopCloseTimerRef.current = setTimeout(() => {
          handleClose();
        }, 5000);
      }
    }

    return () => {
      if (flipBackTimerRef.current) clearTimeout(flipBackTimerRef.current);
      if (desktopCloseTimerRef.current) clearTimeout(desktopCloseTimerRef.current);
    };
  }, [isOpen, isFlipped, isMobile, hasSubmitted]);




  useEffect(() => {
    if (!isOpen) {
      // Clear all timers
      if (flipBackTimerRef.current) clearTimeout(flipBackTimerRef.current);
      if (desktopCloseTimerRef.current) clearTimeout(desktopCloseTimerRef.current);

      // Reset state
      setHasSubmitted(false);
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setTouched({});
      setIsSubmitting(false);
      setIsFlipped(false);
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setTouched({});
    setHasSubmitted(false);
    setIsSubmitting(false);
  };

  // Also update the handleClose function to clear the timer
  const handleClose = () => {
    if (flipBackTimerRef.current) {
      clearTimeout(flipBackTimerRef.current);
      flipBackTimerRef.current = null;
    }
    if (desktopCloseTimerRef.current) {
      clearTimeout(desktopCloseTimerRef.current);
      desktopCloseTimerRef.current = null;
    }
    setIsFlipped(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const newTouched = Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(newTouched);

    const newErrors: Partial<WaitlistFormData> = {};
    let isValid = true;
    Object.keys(formData).forEach(key => {
      const field = key as keyof WaitlistFormData;
      const error = validators[field](formData[field]);
      if (error) { newErrors[field] = error; isValid = false; }
    });

    setErrors(newErrors);

    if (isValid) {
      onSubmit(formData);
      let { fullName, phoneNumber, emailAddress, investsInMF, monthlyIncome } = formData

      const userId = getOrCreateUserId();
      sendEvent(userId, "UNLOCK_YOUR_INVITE", null, fullName, phoneNumber, emailAddress, investsInMF, monthlyIncome).catch(() => { });
      setHasSubmitted(true); // Set to true to show success state

      if (isMobile) {
        setIsFlipped(true);
        // Mobile: Don't reset form here, let the timer handle it
      }

      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setTouched({});
    }

    setIsSubmitting(false);
  };

  const handleChange = (field: keyof WaitlistFormData, value: string) => {
    const processor = fieldProcessors[field as keyof typeof fieldProcessors];
    const processedValue = processor ? processor(value) : value;
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validators[field](processedValue) || undefined }));
    }
  };

  const handleBlur = (field: keyof WaitlistFormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validators[field](formData[field]) || undefined }));
  };

  const renderFormSide = () => (
    <DialogContent sx={{
      p: 0,
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      bgcolor: { xs: "#1D1D21", md: "#111111" },
      overflow: "hidden",
      height: "100%",
      pt: { xs: "24px", md: 0 },
    }}>
      {!isMobile && (
        <Box sx={{
          width: "60%",
          position: "relative",
          backgroundImage: `url(${LightsImage})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left top",
          minHeight: { md: "500px" }
        }}>
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            p: { md: 3, lg: 4 },
            height: "100%"
          }}>
            {hasSubmitted ? (
              // After submission: Show Success GIF with Text
              <Box sx={{
                width: "100%",
                maxWidth: "280px",
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 4
              }}>
                <Lottie
                  loop={true}
                  autoplay={true}
                  animationData={SuccessLottie}
                  style={{
                    width: "280px",
                    height: "280px",
                    marginBottom: "1rem"
                  }}
                />
                <Typography variant="h3" sx={{
                  color: "#FFFFFF",
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontFamily: "Plus Jakarta Sans",
                  fontSize: "1.5rem",
                  lineHeight: 1.3
                }}>
                  Thanks for Applying! <br />
                  <span style={{
                    fontSize: "0.875rem",
                    fontWeight: "regular",
                    color: "#FFFFFF99",
                    display: "block",
                    marginTop: "0.25rem"
                  }}>
                    You are added to the waitlist.  <br /> <br/>
                    Our team will review your profile and connect with you within 1 working day
                  </span>
                </Typography>
              </Box>
            ) : (
              // Before submission: Show only Unlock Box Image (no text)
              <Box sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%"
              }}>
                <img
                  src={UnlockBoxImage}
                  alt="Unlock Box"
                  style={{
                    width: "280px",
                    height: "280px",
                    objectFit: "contain",
                    maxWidth: "80%",
                    maxHeight: "80%",
                    bottom: 0,
                    position: 'absolute'
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{
        width: { xs: "100%", md: "40%" },
        p: { xs: 3, md: 4 },
        bgcolor: { xs: "#1D1D21", md: "#111111" },
        display: "flex",
        flexDirection: "column",
        borderLeft: { md: "1px solid rgba(255, 255, 255, 0.1)" },
        overflowY: "auto"
      }}>
        <DialogTitle sx={{
          bgcolor: { xs: "#1D1D21", md: "#111111" },
          position: "relative",
          px: 0,
          pb: 2,
          pt: { xs: 0, md: 1 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%"
        }}>
          <Typography variant="h1" fontWeight={{ xs: "regular", md: "medium" }} color="#FFFFFF"
            fontFamily="Plus Jakarta Sans"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
              lineHeight: { xs: "1.2", md: "1.3" },
              flex: 1,
              mr: 1
            }}>
            Join the Club
          </Typography>
          <IconButton onClick={handleClose} sx={{
            color: "#FFFFFF99",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
            p: { xs: 0.75, sm: 1 },
            mt: -0.5
          }}>
            <CloseIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
          </IconButton>
        </DialogTitle>

        <Typography variant="body1" sx={{
          mb: 3,
          color: "#FFFFFF99",
          fontWeight: "regular",
          fontFamily: 'Plus Jakarta Sans',
          fontSize: { xs: "0.875rem", md: "1rem" },
          lineHeight: { xs: "1.5", md: "1.6" }
        }}>
          Sign up now for early access to KaratClub's member benefits.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 3 },
          flex: 1
        }}>
          {FORM_FIELDS.map(({ key, label, type, placeholder, inputProps }) => (
            <TextField
              key={key}
              label={label}
              type={type}
              autoComplete={type === "email" ? "new-email" : "off"}
              value={formData[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              onBlur={() => handleBlur(key)}
              error={touched[key] && !!errors[key]}
              helperText={touched[key] && errors[key]}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: 52, md: 52 },
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  "& fieldset": { borderColor: "#FFFFFFB8", borderWidth: "1px" },
                  "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.5)" },
                  "&.Mui-focused fieldset": { borderColor: "#FFFFFF" },
                  "& input": {
                    color: "#FFFFFF",
                    fontFamily: "Plus Jakarta Sans",
                    fontWeight: "medium",
                    fontSize: { xs: "0.9375rem", md: "1rem" },
                    padding: { xs: "14px 16px", md: "14px 16px" },
                    height: "auto"
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#FFFFFF99",
                  fontFamily: "Plus Jakarta Sans",
                  fontWeight: "medium",
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  backgroundColor: "#201D1A",
                  borderRadius: "4px",
                  padding: "2px 4px",
                  "&.Mui-focused": { color: "#FFFFFF", backgroundColor: "#201D1A" },
                  "&.MuiInputLabel-shrink": { transform: 'translate(14px, -9px) scale(0.75)' },
                },
                "& .MuiFormHelperText-root": {
                  color: "#f44336",
                  fontWeight: "medium",
                  fontFamily: "Plus Jakarta Sans",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  marginLeft: 0,
                  marginTop: 0.5,
                  lineHeight: 1.2
                },
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                placeholder,
                sx: {
                  "&::placeholder": {
                    color: "#FFFFFF99",
                    fontFamily: 'Plus Jakarta Sans',
                    fontWeight: "medium",
                    opacity: 1,
                    fontSize: { xs: "0.9375rem", md: "1rem" }
                  }
                }
              }}
              {...(inputProps && { inputProps })}
            />
          ))}

          <FormControl component="fieldset" error={touched.investsInMF && !!errors.investsInMF} sx={{ mt: 1 }}>
            <FormLabel focused={false} sx={{
              color: touched.investsInMF && errors.investsInMF ? "#f44336" : "#FFFFFF99",
              fontFamily: 'Plus Jakarta Sans',
              fontSize: { xs: "0.9375rem", md: "1rem" },
              mb: 1.5
            }}>
              Do you currently invest in mutual funds?
            </FormLabel>
            <Box sx={{ display: "flex", gap: { xs: "30%", md: "40%" } }}>
              {["yes", "no"].map((value) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={formData.investsInMF === value}
                      onChange={(e) => {
                        handleChange("investsInMF", e.target.checked ? value : "");
                        if (e.target.checked && errors.investsInMF) setErrors(prev => ({ ...prev, investsInMF: undefined }));
                      }}
                      onBlur={() => handleBlur("investsInMF")}
                      icon={
                        <Box sx={{
                          width: { xs: 22, md: 24 },
                          height: { xs: 22, md: 24 },
                          border: `1px solid ${touched.investsInMF && errors.investsInMF ? "#f44336" : "#50505D"}`,
                          borderRadius: 1,
                          transition: "border-color 0.2s ease"
                        }} />
                      }
                      checkedIcon={
                        <Box sx={{
                          width: { xs: 22, md: 24 },
                          height: { xs: 22, md: 24 },
                          border: "1px solid #DBAD71",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(219, 173, 113, 0.1)"
                        }}>
                          <DoneIcon sx={{ color: "#DBAD71", fontSize: { xs: 16, md: 16 } }} />
                        </Box>
                      }
                      sx={{
                        p: 0,
                        "&:hover": { bgcolor: "transparent" },
                        "&.Mui-checked:hover": { bgcolor: "transparent" }
                      }}
                      disableRipple
                    />
                  }
                  label={
                    <Typography sx={{
                      color: "#FFFFFF",
                      fontWeight: "medium",
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: { xs: "0.9375rem", md: "1rem" },
                      ml: 1,
                      textTransform: "capitalize"
                    }}>
                      {value}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              ))}
            </Box>
            {touched.investsInMF && errors.investsInMF && <FormHelperText>{errors.investsInMF}</FormHelperText>}
          </FormControl>

          <TextField
            select
            label="Annual Income Range (Optional)"
            value={formData.monthlyIncome}
            onChange={(e) => handleChange("monthlyIncome", e.target.value)}
            onBlur={() => handleBlur("monthlyIncome")}
            error={touched.monthlyIncome && !!errors.monthlyIncome}
            helperText={touched.monthlyIncome && errors.monthlyIncome}
            fullWidth
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "#FFFFFF99" }}>
                      Annual Income Range
                    </span>
                  );
                }
                return MONTHLY_INCOME_OPTIONS.find(
                  (o) => o.value === selected
                )?.label;
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    bgcolor: "#111111",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    maxHeight: "300px",
                    "& .MuiMenuItem-root": {
                      color: "#FFFFFF",
                      fontWeight: "medium",
                      fontFamily: "Plus Jakarta Sans",
                      fontSize: { xs: "0.9375rem", md: "1rem" },
                      py: 1,
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                      "&.Mui-selected": { bgcolor: "rgba(255, 255, 255, 0.15)" },
                    },
                  },
                },
              },
            }}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                height: 52,
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                "& fieldset": {
                  borderColor: "#FFFFFFB8",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FFFFFF",
                },
              },

              /* TEXT */
              "& .MuiSelect-select": {
                color: "#FFFFFF",
                fontFamily: "Plus Jakarta Sans",
                fontWeight: "medium",
                fontSize: { xs: "0.9375rem", md: "1rem" },
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
              },

              /* SELECT ARROW ICON */
              "& .MuiSelect-icon": {
                color: "#FFFFFF99",
                right: "12px",
              },

              /* LABEL */
              "& .MuiInputLabel-root": {
                color: "#FFFFFF99",
                fontFamily: "Plus Jakarta Sans",
                fontWeight: "medium",
                fontSize: { xs: "0.9375rem", md: "1rem" },
                backgroundColor: "#201D1A",
                borderRadius: "4px",
                padding: "2px 4px",
                "&.Mui-focused": {
                  color: "#FFFFFF",
                  backgroundColor: "#201D1A",
                },
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          >
            <MenuItem value="">
              <em
                style={{
                  color: "#FFFFFF99",
                  fontStyle: "normal",
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                Annual Income Range
              </em>
            </MenuItem>

            {MONTHLY_INCOME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>


          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              sx={{
                py: { xs: 1.25, md: 1.25 },
                fontSize: { xs: "1rem", md: "1.125rem" },
                fontWeight: 600,
                textTransform: "none",
                fontFamily: 'Plus Jakarta Sans',
                bgcolor: "#DBAD71",
                color: "#111111",
                border: "1px solid #FFFFFF",
                borderRadius: "8px",
                "&:hover": {
                  background: "#DBAD71",
                  border: "0.66px solid #B5834E",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(219, 173, 113, 0.2)"
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(219, 173, 113, 0.5)",
                  color: "rgba(17, 17, 17, 0.5)",
                  borderColor: "rgba(255, 255, 255, 0.3)"
                },
                transition: "all 0.2s ease"
              }}
            >
              {isSubmitting ? "Submitting..." : "Unlock Your Invite"}
            </Button>
          </Box>
        </Box>
      </Box>
    </DialogContent>
  );

  const renderThankYouSide = () => (
    <Box sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 3,
      // backgroundImage: `url(${LightsImage})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center"
    }}>
      <Box sx={{
        width: "100%",
        // maxWidth: "280px",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        bgcolor: "rgba(29, 29, 33, 0.8)",
        borderRadius: "12px",
        // p: 4,
        backdropFilter: "blur(10px)",
      }}>
        <Lottie
          loop={true}
          autoplay={true}
          animationData={SuccessLottie}
          style={{ width: "160px", height: "160px", marginBottom: "1rem" }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "#FFFFFF",
            textAlign: 'center',
            fontWeight: 'bold',
            fontFamily: "Plus Jakarta Sans",
            fontSize: "1.25rem",
            lineHeight: 1.4,
            mt: 2
          }}
        >
          Thanks for Applying! <br />
          <span style={{
            fontSize: "0.875rem",
            fontWeight: "regular",
            color: "#FFFFFF99",
            display: "block",
            marginTop: "0.5rem"
          }}>
            You are added to the waitlist.  <br /> <br/>
            Our team will review your profile and connect with you within 1 working day
          </span>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(4px)"
        }
      }}
      PaperProps={{
        sx: {
          bgcolor: { xs: "#1D1D21", md: "#111111" },
          borderRadius: { xs: "20px", sm: 3 },
          boxShadow: 24,
          overflow: "hidden",
          margin: { xs: 0, sm: 3 },
          maxHeight: { xs: "95vh", md: "85vh" },
          maxWidth: { xs: "95vw", md: "900px" },
          width: { xs: "95%", md: undefined },
          height: "100%",
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '36px',
            height: '4px',
            backgroundColor: '#50505D',
            borderRadius: '2.5px',
            zIndex: 1,
            display: { xs: 'block', md: 'none' }
          }
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* FRONT SIDE - FORM */}
        <Box sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backfaceVisibility: 'hidden',
          overflow: 'hidden',
        }}>
          {renderFormSide()}
        </Box>

        {/* BACK SIDE - THANK YOU (Mobile only) */}
        {isMobile && (
          <Box sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            overflow: 'hidden',
          }}>
            {renderThankYouSide()}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default WaitlistPopup;